import { RelationObjectType } from "@enums/relations.enum";
import {
  AttributeType,
  IAttribute,
  IAttributeFieldValue,
  IAttributeFieldValuePayload,
  IAttributeValue,
  IRelationObject,
} from "@interfaces/attribute.interface";
import { isMaskInputVariant, MASK_INPUT_VARIANTS } from "@ui/FormElements/MaskInput.config";
import { addUrlProtocol, validateURL } from "@utils/strings.util";

import {
  DROPDOWN_ATTRIBUTE_TYPES,
  isRelationAttribute,
  MAX_YEAR_OFFSET,
  MIN_YEAR,
  NUMBER_ATTRIBUTE_TYPES,
  TEXT_ATTRIBUTE_TYPES,
} from "./constants";
import { haveSameRelations, toRelationObjectPayloads } from "./RelationAttribute/utils";

/** Keeps attributes applicable to a company, sorted by (type-specific) order. */
export function filterAttributesForCompany(attributes: IAttribute[], type?: string): IAttribute[] {
  const orderFor = (attr: IAttribute): number =>
    type
      ? (attr.company_types.find((t) => t.type === type)?.order ?? Infinity)
      : (attr.applicable_models.find((m) => m.content_type === RelationObjectType.COMPANY)?.order ??
        Infinity);

  const res = attributes
    .filter(
      (attr) =>
        attr.applicable_models.some((m) => m.content_type === RelationObjectType.COMPANY) &&
        (type ? attr.company_types.some((t) => t.type === type) : true)
    )
    .sort((a, b) => orderFor(a) - orderFor(b));

  if (!type) return res;

  // Narrow dropdown options to those allowed for the selected company type.
  return res.map((attr) =>
    attr.dropdown_options
      ? {
          ...attr,
          dropdown_options: attr.dropdown_options.filter(
            (o) =>
              !o.company_types || o.company_types.length === 0 || o.company_types.includes(type)
          ),
        }
      : attr
  );
}

/** Keeps attributes applicable to a deal, sorted by order. */
export function filterAttributesForDeal(attributes: IAttribute[]): IAttribute[] {
  return attributes
    .filter((attr) =>
      attr.applicable_models.some((m) => m.content_type === RelationObjectType.DEAL)
    )
    .sort((a, b) => {
      const orderA =
        a.applicable_models.find((m) => m.content_type === RelationObjectType.DEAL)?.order ??
        Infinity;
      const orderB =
        b.applicable_models.find((m) => m.content_type === RelationObjectType.DEAL)?.order ??
        Infinity;
      return orderA - orderB;
    });
}

/** Attribute ids applicable to BOTH company and deal — i.e. shared/common attributes. */
export function getCommonAttributeIds(attributes: IAttribute[]): Set<string> {
  const ids = new Set<string>();
  for (const attribute of attributes) {
    const contentTypes = attribute.applicable_models.map((m) => m.content_type);
    if (
      contentTypes.includes(RelationObjectType.COMPANY) &&
      contentTypes.includes(RelationObjectType.DEAL)
    ) {
      ids.add(attribute.id);
    }
  }
  return ids;
}

/** Parses a stored `YYYY-MM-DD` value; midday keeps it on the same day in every timezone. */
export function parseAttributeDate(value: string): Date | null {
  if (!value) return null;

  const date = new Date(`${value}T12:00:00`);

  return isNaN(date.getTime()) ? null : date;
}

/** Serialises a picked date back to the `YYYY-MM-DD` the API stores. */
export function formatAttributeDate(date: Date | null): string {
  if (!date || isNaN(date.getTime())) return "";

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${date.getFullYear()}-${month}-${day}`;
}

/** Field placeholder: the masked variant's numeric hint where there is one, else "Add {title}". */
export function getAttributePlaceholder(attribute: IAttribute): string {
  if (isMaskInputVariant(attribute.type)) return MASK_INPUT_VARIANTS[attribute.type].placeholder;

  return `Add ${attribute.title}`;
}

/**
 * HTML input type backing an attribute's text field. Currency and percentage stay "text": they
 * render grouped digits and a suffix, which a number input would reject.
 */
export function getAttributeInputType(type: AttributeType): "url" | "date" | "number" | "text" {
  if (type === AttributeType.URL) return "url";
  if (type === AttributeType.DATE) return "date";
  if (NUMBER_ATTRIBUTE_TYPES.includes(type)) return "number";

  return "text";
}

/** Validates a value for its attribute type. Returns true, or an error message. */
export function validateAttributeValue(attribute: IAttribute, value: string): true | string {
  // Relation fields have no scalar value; the picker only yields existing records.
  if (isRelationAttribute(attribute.type)) return true;

  if (!value) return true;

  if (attribute.type === AttributeType.CURRENCY) return true;

  if (attribute.type === AttributeType.CREDIT) {
    if (!/^(L\+|S\+)?-?\d+(\.\d+)?$/.test(value)) {
      return "Value must be in the format L+X.XX, S+X.XX, or X.XX. Please try again.";
    }
    return true;
  }

  if (TEXT_ATTRIBUTE_TYPES.includes(attribute.type)) {
    const trimmed = value.trim();
    if (trimmed.length === 0) return "Input cannot be just spaces.";
    if (trimmed.length < 2) return "Input too short. Minimum 2 characters required.";
    if (trimmed.length > 255) return "Input too long. Maximum 255 characters allowed.";
    return true;
  }

  if (attribute.type === AttributeType.URL && !validateURL(addUrlProtocol(value))) {
    return "Please enter a valid URL";
  }

  if (NUMBER_ATTRIBUTE_TYPES.includes(attribute.type) && isNaN(Number(value))) {
    return "Please enter a valid number";
  }

  if (
    [AttributeType.NUMBER_NOT_NEGATIVE, AttributeType.YEAR].includes(attribute.type) &&
    Number(value) < 0
  ) {
    return "Please enter a valid number";
  }

  const maxYear = new Date().getFullYear() + MAX_YEAR_OFFSET;

  if (
    attribute.type === AttributeType.YEAR &&
    (Number(value) < MIN_YEAR ||
      Number(value) > maxYear ||
      value.includes(".") ||
      value.includes(","))
  ) {
    return `Enter a valid year between ${MIN_YEAR}-${maxYear}`;
  }

  return true;
}

/** Has something worth sending. Relation fields are empty when `relations` is. */
function hasAttributeContent(fieldValue: IAttributeFieldValue, attribute: IAttribute): boolean {
  if (isRelationAttribute(attribute.type)) return !!fieldValue.relations?.length;

  return !!fieldValue.value;
}

/**
 * Filters form values to those matching the given attributes and normalizes them for the API:
 * prepends the URL protocol, extracts the credit prefix, converts dropdown values into
 * `selected_options`, and reduces relations to `{content_type, object_id}` pairs. Pass
 * `allowEmptyValues` to keep cleared fields (used for inline save).
 */
export function filterAndProcessAttributeValues(
  attributeValues: IAttributeFieldValue,
  attributes: IAttribute[],
  options?: { allowEmptyValues?: boolean }
): IAttributeFieldValuePayload;
export function filterAndProcessAttributeValues(
  attributeValues: IAttributeFieldValue[],
  attributes: IAttribute[],
  options?: { allowEmptyValues?: boolean }
): IAttributeFieldValuePayload[];
export function filterAndProcessAttributeValues(
  attributeValues: IAttributeFieldValue | IAttributeFieldValue[],
  attributes: IAttribute[],
  options: { allowEmptyValues?: boolean } = {}
): IAttributeFieldValuePayload | IAttributeFieldValuePayload[] {
  const values = Array.isArray(attributeValues) ? attributeValues : [attributeValues];

  const filtered = values
    .filter((v) => {
      const attribute = attributes.find((a) => a.id === v.attribute);
      if (!attribute) return false;

      return hasAttributeContent(v, attribute) || !!options.allowEmptyValues;
    })
    .map<IAttributeFieldValuePayload>((v) => {
      const attribute = attributes.find((a) => a.id === v.attribute);
      // Split off up front: only the relation branch has relations to send.
      const { relations, ...rest } = v;

      if (!attribute) return rest;

      if (isRelationAttribute(attribute.type)) {
        // An empty array clears the field.
        return { ...rest, value: "", relations: toRelationObjectPayloads(relations ?? []) };
      }

      if (attribute.type === AttributeType.URL) {
        return { ...rest, value: rest.value ? addUrlProtocol(rest.value) : rest.value };
      }

      if (attribute.type === AttributeType.CREDIT) {
        const prefix = rest.value.match(/^[LS]\+/)?.[0] || "";
        return { ...rest, value: rest.value.replace(/^[LS]\+/, ""), prefix };
      }

      if (DROPDOWN_ATTRIBUTE_TYPES.includes(attribute.type)) {
        return { ...rest, value: "", selected_options: rest.value.split(",").filter(Boolean) };
      }

      return rest;
    });

  return Array.isArray(attributeValues) ? filtered : filtered[0];
}

/** Converts an API attribute value into the form representation. */
export function attributeValueToFormValue(
  attributeValue: Pick<
    IAttributeValue,
    "attribute" | "selected_options" | "value" | "prefix" | "relations"
  >,
  attributes: IAttribute[]
): IAttributeFieldValue {
  const attribute = attributes.find((a) => a.id === attributeValue.attribute);

  if (attribute && isRelationAttribute(attribute.type)) {
    return {
      attribute: attributeValue.attribute,
      value: "",
      relations: attributeValue.relations ?? [],
    };
  }

  if (attribute && DROPDOWN_ATTRIBUTE_TYPES.includes(attribute.type)) {
    return {
      attribute: attributeValue.attribute,
      value: (attributeValue.selected_options ?? []).join(","),
    };
  }

  return {
    attribute: attributeValue.attribute,
    value: `${attributeValue.prefix ?? ""}${attributeValue.value ?? ""}`,
  };
}

/** Builds one form entry per attribute, pre-filled from existing values when present. */
export function generateDefaultAttributeFieldValues(
  attributes: IAttribute[],
  existingValues?: IAttributeValue[]
): IAttributeFieldValue[] {
  return attributes.map((attribute) => {
    const existing = existingValues?.find((v) => v.attribute === attribute.id);
    if (existing) return attributeValueToFormValue(existing, attributes);

    // Always an array, so the picker has something to append to.
    return isRelationAttribute(attribute.type)
      ? { attribute: attribute.id, value: "", relations: [] }
      : { attribute: attribute.id, value: "" };
  });
}

/**
 * Of the attributes in `ids`, those whose value differs between the two sides, as indexes into
 * `target`. Used both to mirror shared values between forms and to spot what a form changed.
 */
export function getAttributeValueUpdates(
  source: IAttributeFieldValue[],
  target: IAttributeFieldValue[],
  ids: Set<string>
): { index: number; value: string; relations?: IRelationObject[] }[] {
  return target.flatMap((field, index) => {
    if (!ids.has(field.attribute)) return [];

    const match = source.find((item) => item.attribute === field.attribute);
    if (!match) return [];

    // Recognised by shape: only a relation field carries a `relations` array.
    const isRelation = Array.isArray(match.relations) || Array.isArray(field.relations);

    if (isRelation) {
      if (haveSameRelations(match.relations ?? [], field.relations ?? [])) return [];

      return [{ index, value: match.value, relations: match.relations ?? [] }];
    }

    if (match.value === field.value) return [];

    return [{ index, value: match.value }];
  });
}
